import React, { useContext, useEffect, useState } from 'react';
import Head from 'next/head';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import Colors from 'pubweb-smokey/dist/colors';
import ScrollUpButtonContainer from '@components/Shared/ScrollUpButton/ScrollUpButtonContainer';
import { LocationsContext } from '@contexts/LocationsContext';
import { getDynamicPageContent } from '@services/contentfulService';
import { FindAHomeStyles } from './FindAHome.styled';
import SortBar from '@components/FindAHome/Sorting/SortBar';
import RecentlyViewedHomes from '@components/RecentlyViewedHomes/RecentlyViewedHomes';
import { getSiteUrl } from '@utils/config';
import CuratedButtonGroup from '@components/CuratedCollections/CuratedButtonGroup';
import DynamicDisclaimer from '@components/Shared/Disclaimer/DynamicDisclaimer';
import {
  DEFAULT_SORT_METHOD_ID,
  MODELS_PER_PAGE,
} from '@utils/homeSearchConstants';
import { useHomeSearchParams } from '@hooks/FindAHome/useHomeSearchParams';
import { useStickyFilter } from '@hooks/FindAHome/useStickyFilter';
import HomeStream from '@components/CuratedCollections/HomeStream';
import { FindAHomeContentfulData } from 'types/FindAHome/FindAHomeContentfulData';
import HomeCardsList from '@components/FindAHome/HomeCardsList';
import { useGetHomes } from '@hooks/FindAHome/useGetHomes';
import { LocationContextState } from 'types/FindAHome/LocationContextState';
import { LocationProps } from 'types/FindAHome/HomeSearchProps';
import Paginator from '@components/Shared/Paginator/Paginator';
import LoadingSkeleton from '@components/FindAHome/LoadingSkeleton';

// NOTE: Causes hydration errors on mobile since the pill text is different than on desktop.
const FiltersBar = dynamic(
  () => import('@components/FindAHome/Filters/FiltersBar'),
  { ssr: false }
);

const FindAHomeNoListings = dynamic(
  () => import('@components/FindAHome/FindAHomeNoListings'),
  { ssr: false }
);

const FindAHomeNoLocations = dynamic(
  () => import('@components/FindAHome/FindAHomeNoLocations'),
  { ssr: false }
);

const FindAHomeError = dynamic(
  () => import('@components/FindAHome/FindAHomeError'),
  { ssr: false }
);

const FindAHome = ({ data }: { data: FindAHomeContentfulData }) => {
  const [isBrowser, setIsBrowser] = useState(false);
  useEffect(() => {
    setIsBrowser(true);
  }, []);

  const stickyFilter = useStickyFilter();
  const router = useRouter();
  const [locationFilterShowing, setLocationFilterShowing] = useState(false);

  const locationsContext = useContext(
    LocationsContext
  ) as unknown as LocationContextState; // TODO: If LocationsContext is converted to TypeScript, should update this. This is a hack.
  const { distance, updating: isLocationUpdating } = locationsContext.state;
  const { city, state, postalCode, latitude, longitude } =
    locationsContext.state.geocode ?? {};
  const hasLocation =
    postalCode && city && state && longitude && latitude && distance;

  // NOTE: Location data is needed for this page to function.
  // If location data does not exist, we will fetch it here!
  useEffect(() => {
    if (!hasLocation) {
      locationsContext.actions.getLocationInfo();
    }

    // NOTE: This linting rule is disabled because `locationsContext.actions.getLocationInfo` will
    // cause this effect to be called on every render. So, this only runs on component mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const locationProps: LocationProps = {
    city,
    state,
    postalCode,
    distance,
    longitude,
    latitude,
    isLocationUpdating,
  };

  const { updateFilterParams, updatePageNumberParam, ...filterProps } =
    useHomeSearchParams(locationProps);

  const {
    data: homeList,
    isPending,
    isFetching,
    isSuccess,
    isError,
    refetch,
  } = useGetHomes({
    ...filterProps,
    ...locationProps,
  });

  const {
    isMoveInReady,
    beds,
    baths,
    minPrice,
    maxPrice,
    minSquareFeet,
    maxSquareFeet,
    section,
    features,
    sortId,
    page,
  } = filterProps;

  const selectedFeatures = features ? features.split(',') : [];
  const totalResults = homeList?.paginationResults.totalResults;

  // Successfully returned home models and there is at least 1 model to show.
  const hasModels =
    isSuccess &&
    homeList != null &&
    homeList.models.length > 0 &&
    totalResults != null;

  // Successfully returned home models, but there are 0 models to show.
  const showNoListings =
    isSuccess &&
    !isFetching &&
    homeList != null &&
    homeList.models.length === 0;

  const showError = !isFetching && isError;

  // --------------------------------------------------------------------------------------------------------------------
  // NOTE: To prevent hydration error issues and ensure a smooth initial loading experience for users,
  // we don't want the server deciding whether to show the noLocations component or the loadingSkeleton component.
  // We ALWAYS want the server to render the loadingSkeleton and NEVER render the noLocations component on initial load
  // because the server does not currently have any information about whether the user has a location or not.
  // Once we are running code in the browser, we can decide to show the noLocations component if we are missing a location.

  // Only shows the noLocations component on the client when the user does not have a location set. See NOTE above.
  const showNoLocations =
    isBrowser &&
    !isLocationUpdating &&
    !isFetching &&
    !hasModels &&
    !hasLocation;

  // Always render the loadingSkeleton component server side. See NOTE above.
  // Otherwise, we can show this component whenever we are fetching model data from the API and
  // we don't have any models to show in the background while the user waits.
  const showLoadingSkeleton = (isPending && !showNoLocations) || isFetching;
  // --------------------------------------------------------------------------------------------------------------------

  function scrollToTop(): void {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }

  return (
    <FindAHomeStyles>
      <Head>
        <title>{data.titleTag}</title>
        <meta name="description" content={data.metaDescription} />
        <link rel="canonical" href={`${getSiteUrl(router.asPath)}`} />
        <meta
          property="og:image"
          content={
            typeof data.ogImage !== 'undefined' && data.ogImage !== null
              ? data.ogImage.file.url
              : `${getSiteUrl('/public-assets/images/clayton-logo.jpg')}`
          }
        />
      </Head>

      <ScrollUpButtonContainer />

      <div className={'filters-bar-container ' + (stickyFilter && 'sticky')}>
        <FiltersBar
          beds={beds}
          baths={baths}
          minPrice={minPrice}
          maxPrice={maxPrice}
          minSquareFeet={minSquareFeet}
          maxSquareFeet={maxSquareFeet}
          section={section}
          selectedFeatures={selectedFeatures}
          isMoveInReady={isMoveInReady}
          locationFilterShowing={locationFilterShowing}
          setLocationFilterShowing={setLocationFilterShowing}
          onPostalCodeChange={() => {
            updateFilterParams({});
            scrollToTop();
          }}
          onApply={(params) => {
            updateFilterParams({ ...params });
            scrollToTop();
          }}
        />
      </div>

      <h1 className="page-header label large">Find A Home</h1>

      {data?.curatedCollectionButtons?.length > 0 && (
        <CuratedButtonGroup
          heading="Curated Collections"
          buttonInfo={
            data.curatedCollectionButtons ? data.curatedCollectionButtons : null
          }
        />
      )}
      {data.curatedCollectionStreams && data.curatedCollectionStreams[0] && (
        <HomeStream
          key="home-stream-1"
          stream={data.curatedCollectionStreams[0]}
        />
      )}
      {data.curatedCollectionStreams && data.curatedCollectionStreams[1] && (
        <HomeStream
          key="home-stream-2"
          stream={data.curatedCollectionStreams[1]}
        />
      )}
      {data.curatedCollectionStreams && data.curatedCollectionStreams[2] && (
        <HomeStream
          key="home-stream-3"
          stream={data.curatedCollectionStreams[2]}
        />
      )}

      <div className="page-content" key="page-content">
        {showLoadingSkeleton && <LoadingSkeleton />}
        {showNoListings && (
          <FindAHomeNoListings
            onExpandRange={() => setLocationFilterShowing(true)}
          />
        )}
        {showNoLocations && (
          <FindAHomeNoLocations
            onExpandRange={() => setLocationFilterShowing(true)}
            postalCode={postalCode}
          />
        )}
        {showError && (
          <FindAHomeError
            onRetry={() => {
              refetch();
              scrollToTop();
            }}
          />
        )}
        {hasModels && (
          <div>
            <div className="sort-bar-container">
              <SortBar
                numResults={totalResults}
                onSort={(value) => {
                  updateFilterParams({ sortId: value });
                  scrollToTop();
                }}
                sortId={sortId}
                defaultSortId={DEFAULT_SORT_METHOD_ID}
              />
            </div>

            <div>
              <HomeCardsList data={data} homeList={homeList} page={page} />
            </div>

            {totalResults > MODELS_PER_PAGE && (
              <Paginator
                currentPage={page}
                isDisabled={isFetching}
                totalItems={totalResults}
                itemsPerPage={MODELS_PER_PAGE}
                onClickNewPage={(newPage) => {
                  updatePageNumberParam(newPage);
                  scrollToTop();
                }}
              />
            )}

            <div className="disclaimer">
              <DynamicDisclaimer
                textColor={`${Colors.accent.grey1.standard}`}
                backgroundColor={`${Colors.primary.white.standard}`}
                disclaimerContent="\* Advertised starting sales prices are for the home only. Delivery and installation costs are not included unless otherwise stated.
                Starting prices shown on this website are subject to change, see your local Home Center for current and specific home and pricing information.
                Sales price does not include other costs such as taxes, title fees, insurance premiums, filing or recording fees, land or improvements to the land,
                optional home features, optional delivery or installation services, wheels and axles, community or homeowner association fees, or any other items not
                shown on your Sales Agreement, Retailer Closing Agreement and related documents (your SA/RCA). If you purchase a home, your SA/RCA will show the details
                of your purchase. Homes available at the advertised sales price will vary by retailer and state. Artists’ renderings of homes are only representations
                and actual home may vary. Floor plan dimensions are approximate and based on length and width measurements from exterior wall to exterior wall.
                We invest in continuous product and process improvement. All home series, floor plans, specifications, dimensions, features, materials, and availability
                shown on this website are subject to change. Images may show options not included in base price. Does not include required taxes, delivery, and installation. Installed price will be higher.
                <br><br>**[ZERH Logo Use Guidelines (energy.gov)](https://www.energy.gov/sites/default/files/2022-11/ZERH%20Name%20and%20Logo%20Use%20Guidelines_0.pdf)"
              />
            </div>
          </div>
        )}
      </div>

      <hr />

      <div className="page-content">
        <div className="recently-viewed-container">
          <RecentlyViewedHomes />
        </div>
      </div>
    </FindAHomeStyles>
  );
};

FindAHome.getInitialProps = async () => {
  const contentfulKey = '5uxXdxgZAN7S4FM6aLBNKC';
  const data = await getDynamicPageContent(contentfulKey);
  return { data };
};

export default FindAHome;
